import React, { useEffect, useMemo, useState, useCallback } from "react";
import NavigationBar from "./NavigationBar";
import { Button } from "./ui/button";
import { apiFetch } from "../lib/api";
import { AlertTriangle } from "lucide-react";
import { useDemoMode } from "../contexts/DemoModeContext";  // match the relative path

/* -------------------- Types -------------------- */
type Resident = {
  id: number;
  name: string;
  room_number: string;
  is_inside: boolean;
  role: string;
  last_seen: string | null;
  elopement_risk: boolean;
  access_risk: boolean;
  visiting_with_id?: number | null;
  visiting_with_name?: string | null;
  visiting_with_room?: string | null;
  last_clip_url?: string | null;
  census_last_seen?: string | null;       // ADD
  census_last_clip_url?: string | null;   // ADD
  census_last_frame_url?: string | null;  // ADD
};

type HoverClip = {
  url: string;
  x: number;
  y: number;
  name: string;
};

export default function ConciergeDashboard() {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [showHeadcount, setShowHeadcount] = useState(false);
  const [showCensus, setShowCensus] = useState(false);
  const [seenTodayOverrides, setSeenTodayOverrides] = useState<Record<number, boolean>>({});
  const [hoverClip, setHoverClip] = useState<HoverClip | null>(null);


  const [headAsOf, setHeadAsOf] = useState<Date | null>(null);
  const [censusAsOf, setCensusAsOf] = useState<Date | null>(null);
  const [censusFrameResident, setCensusFrameResident] = useState<Resident | null>(null);
  const { displayName } = useDemoMode();

  const fetchResidents = useCallback(async () => {
    const res = await apiFetch("/get_residents", { cache: "no-store" });
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    setResidents(data);
  }, []);


  /* -------------------- Initial load -------------------- */
  useEffect(() => {
    apiFetch("/get_residents", { cache: "no-store" })
      .then((res) => res.json())
      .then(setResidents)
      .catch((err) => console.error("Failed to fetch residents:", err));
  }, []);

  useEffect(() => {
    if (showHeadcount) {
      (async () => {
        try { await fetchResidents(); } finally { setHeadAsOf(new Date()); }
      })();
    }
  }, [showHeadcount, fetchResidents]);

  useEffect(() => {
    if (showCensus) {
      (async () => {
        try { await fetchResidents(); } finally { setCensusAsOf(new Date()); }
      })();
    }
  }, [showCensus, fetchResidents]);

  const formatAsOf = (d: Date | null) =>
    d
      ? new Intl.DateTimeFormat("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "America/New_York",
        }).format(d)
      : "--:--";

  /* -------------------- SSE for Resident Updates -------------------- */
  useEffect(() => {
    const esRef = { current: null as EventSource | null };
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const applyUpdate = (raw: any) => {
      if (!raw) return;

      const msg = typeof raw === "string"
          ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
          : raw;
      if (!msg) return;

      if (msg.type === "unknown_visitor") return;

      // === CENSUS UPDATE — only update census fields, never is_inside or last_seen ===
      if (msg.type === "census_update") {
          const idNum = typeof msg.id === "string" ? Number(msg.id) : msg.id;
          setResidents((prev) => {
              const idx = prev.findIndex((r) => r.id === idNum);
              if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = {
                      ...next[idx],
                      census_last_seen: msg.census_last_seen ?? next[idx].census_last_seen,
                      census_last_clip_url: msg.census_last_clip_url ?? next[idx].census_last_clip_url,
                      census_last_frame_url: msg.census_last_frame_url ?? next[idx].census_last_frame_url,
                  };
                  return next;
              }
              return prev;
          });
          return; // ← stops here, never touches Live Sign-In/Sign-Out
      }

    const looksLikeUpdate =
        msg.type === "resident_update" ||
        (("id" in msg) && ("is_inside" in msg));

    if (!looksLikeUpdate) return;

      const idNum = typeof msg.id === "string" ? Number(msg.id) : msg.id;

      setResidents((prev) => {
        const idx = prev.findIndex((r) => r.id === idNum);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            is_inside: msg.is_inside ?? next[idx].is_inside,
            last_seen: msg.last_seen ?? next[idx].last_seen,
            name: msg.name ?? next[idx].name,
            room_number: msg.room_number ?? next[idx].room_number,
            role: msg.role ?? next[idx].role,
            elopement_risk: msg.elopement_risk ?? next[idx].elopement_risk,
            access_risk: msg.access_risk ?? next[idx].access_risk,
            visiting_with_id: msg.visiting_with_id ?? next[idx].visiting_with_id,
            visiting_with_name: msg.visiting_with_name ?? next[idx].visiting_with_name,
            visiting_with_room: msg.visiting_with_room ?? next[idx].visiting_with_room,
            last_clip_url: msg.last_clip_url !== undefined ? msg.last_clip_url : next[idx].last_clip_url,
          };

          next.sort((a, b) => {
            const ta = a.last_seen ? new Date(a.last_seen).getTime() : 0;
            const tb = b.last_seen ? new Date(b.last_seen).getTime() : 0;
            return tb - ta;
          });
          return next;
        } else {
          const inserted: Resident = {
            id: idNum,
            name: msg.name ?? `ID ${idNum}`,
            room_number: msg.room_number ?? "",
            role: msg.role ?? "resident",
            is_inside: !!msg.is_inside,
            last_seen: msg.last_seen ?? null,
            elopement_risk: msg.elopement_risk ?? false,
            access_risk: msg.access_risk ?? false,
            visiting_with_id: msg.visiting_with_id ?? null,
            visiting_with_name: msg.visiting_with_name ?? null,
            visiting_with_room: msg.visiting_with_room ?? null,
            last_clip_url: msg.last_clip_url ?? null,
            census_last_seen: null,
            census_last_clip_url: null,
            census_last_frame_url: null,
          };

          const next = [...prev, inserted];
          next.sort((a, b) => {
            const ta = a.last_seen ? new Date(a.last_seen).getTime() : 0;
            const tb = b.last_seen ? new Date(b.last_seen).getTime() : 0;
            return tb - ta;
          });
          return next;
        }
      });
    };

    const connect = () => {
      if (closed || esRef.current) return;

      const SSE_BASE = process.env.REACT_APP_SSE_URL!;
      const sseUrl = `${SSE_BASE}resident-updates?ts=${Date.now()}`;
      const es = new EventSource(sseUrl, { withCredentials: false });
      esRef.current = es;

      es.onopen = () => {
        console.log("[SSE] open");
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      };

      es.onmessage = (evt) => {
        console.log("[SSE] raw message:", evt.data);
        if (evt.data && !evt.data.includes('"ping"') && !evt.data.includes('ok')) {
          console.debug("[SSE] message:", evt.data);
        }
        applyUpdate(evt.data);
      };

      es.addEventListener("resident_update", (evt: MessageEvent) => {
        console.debug("[SSE] resident_update:", evt.data);
        applyUpdate(evt.data);
      });

      es.addEventListener("ping", () => {});

      es.onerror = () => {
        try { es.close(); } catch {}
        esRef.current = null;
        if (!retryTimer && !closed) retryTimer = setTimeout(connect, 2000);
      };
    };

    const onUnload = () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { esRef.current?.close(); } catch {}
      esRef.current = null;
    };

    window.addEventListener("beforeunload", onUnload);
    if (document.visibilityState === "visible") connect();
    const onVis = () => document.visibilityState === "visible" && connect();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("visibilitychange", onVis);
      onUnload();
    };
  }, []);

  /* -------------------- Hover Handlers -------------------- */
  const handleMouseEnter = (r: Resident, e: React.MouseEvent<HTMLDivElement>) => {
    if (r.last_clip_url) {
      const rect = e.currentTarget.getBoundingClientRect();
      setHoverClip({
        url: `https://api.monivue.io${r.last_clip_url}`,
        x: rect.right + 16,
        y: rect.top,
        name: displayName(r.name),
      });
    }
  };

  const handleMouseLeave = () => {
      setHoverClip(null);
  };

  /* -------------------- Helpers -------------------- */
  const formatDateTime = (iso: string | null) => {
    if (!iso) return { time: "--:--", day: "" };
    const date = new Date(iso);
    const time = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    }).format(date);
    const day = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "America/New_York",
    }).format(date);
    return { time, day };
  };

  const formatTimeAgo = (iso: string | null) => {
    if (!iso) return "";
    const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (isNaN(diffMin)) return "";
    if (diffMin >= 60) return `${Math.floor(diffMin / 60)} h ${diffMin % 60} mn`;
    return `${diffMin} min`;
  };

  const sortedByLatest = useMemo(() => {
    return [...residents].sort((a, b) => {
      const ta = a.last_seen ? new Date(a.last_seen).getTime() : 0;
      const tb = b.last_seen ? new Date(b.last_seen).getTime() : 0;
      return tb - ta;
    });
  }, [residents]);

  const peopleOutside = useMemo(
    () => residents.filter((r) => !r.is_inside && (r.role || "").toLowerCase() === "resident"),
    [residents]
  );

  /* -------------------- Headcount -------------------- */
  const counts = useMemo(() => {
    const lc = (s: string) => (s ? s.toLowerCase() : s);
    const rIn = residents.filter((r) => lc(r.role) === "resident" && r.is_inside).length;
    const rOut = residents.filter((r) => lc(r.role) === "resident" && !r.is_inside).length;
    const sIn = residents.filter((r) => lc(r.role) === "staff" && r.is_inside).length;
    const gIn = residents.filter((r) => lc(r.role) === "visitor" && r.is_inside).length;
    const total = rIn + sIn + gIn;
    return { rIn, rOut, sIn, gIn, total };
  }, [residents]);

  /* -------------------- Daily Census -------------------- */
  const inNYParts = (d: Date) =>
    new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York" })
      .formatToParts(d)
      .reduce((acc, p) => ((acc[p.type] = p.value), acc), {} as Record<string, string>);

  const sameNYDay = (a: Date, b: Date) => {
    const A = inNYParts(a);
    const B = inNYParts(b);
    return A.year === B.year && A.month === B.month && A.day === B.day;
  };

  const residentsOnly = useMemo(
    () => residents.filter((r) => (r.role || "").toLowerCase() === "resident"),
    [residents]
  );

  const isSeenToday = (r: Resident) => {
    const base = r.census_last_seen  // ← changed from r.last_seen
        ? sameNYDay(new Date(r.census_last_seen), new Date())
        : false;
    return seenTodayOverrides[r.id] ?? base;
  };

  const censusStats = useMemo(() => {
    const total = residentsOnly.length;
    let seen = 0;
    for (const r of residentsOnly) if (isSeenToday(r)) seen++;
    return { seen, notSeen: total - seen, total };
  }, [residentsOnly, seenTodayOverrides]);

  const toggleSeen = async (id: number) => {
    try {
        const res = await apiFetch(`/residents/${id}/seen`, { method: "PATCH" });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        setResidents((prev) =>
            prev.map((r) => r.id === id
                ? { ...r, census_last_seen: data.last_seen }  // ← changed from last_seen
                : r
            )
        );
    } catch (err) {
        console.error("Failed to mark resident seen:", err);
    }
  };

  const toggleUnseen = async (id: number) => {
    try {
        const res = await apiFetch(`/residents/${id}/unseen`, { method: "PATCH" });
        if (!res.ok) throw new Error(String(res.status));
        setResidents((prev) =>
            prev.map((r) => r.id === id
                ? {
                    ...r,
                    census_last_seen: null,
                    census_last_frame_url: null,
                    census_last_clip_url: null
                }
                : r
            )
        );
        setCensusFrameResident(null); // close modal
    } catch (err) {
        console.error("Failed to mark resident unseen:", err);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="bg-black min-h-screen text-white">
      <NavigationBar />

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Live Sign-In / Sign-Out */}
        <div className="bg-[#0d1117] rounded-lg p-4 overflow-y-auto max-h-[65vh]">
          <h2 className="text-lg font-semibold mb-4">Live Sign-In / Sign-Out</h2>
          {sortedByLatest.length ? (
            sortedByLatest.map((r) => {
              const role = (r.role || "").toLowerCase();
              const showElopement = role === "resident" && r.elopement_risk;
              const showAccess = role === "visitor" && r.access_risk;

              return (
                  <div
                      key={r.id}
                      className={`flex items-center gap-3 mb-3 p-2 rounded-lg transition-colors ${
                          r.last_clip_url ? "hover:bg-white/5 cursor-pointer" : ""
                      }`}
                      onMouseEnter={(e) => handleMouseEnter(r, e)}
                      onMouseLeave={handleMouseLeave}
                  >
                  <span
                      className={`h-3 w-3 rounded-full ${
                          r.is_inside ? "bg-green-500" : "bg-red-500"
                      }`}
                      title={r.is_inside ? "Inside" : "Outside"}
                  />
                    <div className="text-sm text-white w-[70px] flex flex-col items-start">
                      <span>{formatDateTime(r.last_seen).time}</span>
                      <span className="text-xs text-gray-400">{formatDateTime(r.last_seen).day}</span>
                    </div>
                    <div className="flex flex-col flex-1">
                      <div className="font-semibold flex items-center gap-2">
                        {displayName(r.id, r.name) || <span className="text-amber-400 italic">Unknown Visitor</span>}
                        {r.last_clip_url && (
                            <span className="text-xs text-gray-500">▶</span>
                        )}
                        {showElopement && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-rose-500/40 bg-rose-600/20 text-rose-300">
                          <AlertTriangle className="w-3 h-3"/> Safety Alert
                        </span>
                        )}
                        {showAccess && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-amber-500/40 bg-amber-500/20 text-amber-200">
                          <AlertTriangle className="w-3 h-3"/> Access Risk
                        </span>
                        )}
                      </div>
                      <span className="text-gray-400 text-sm">
                      {role === "resident"
                          ? `(Resident ${r.room_number})`
                          : role === "staff"
                              ? `(Staff)`
                              : r.visiting_with_name
                                  ? `Visiting ${r.visiting_with_name} (Room ${r.visiting_with_room ?? "—"})`
                                  : `(Visitor)`}
                    </span>
                    </div>
                  </div>
              );
            })
          ) : (
              <div className="text-sm text-gray-400">No recent events.</div>
          )}
        </div>

        {/* Residents Out of Community */}
        <div className="bg-[#0d1117] rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Residents Out of Community</h2>
          {peopleOutside.map((r) => (
            <div
              key={r.id}
              className={`flex items-center gap-3 mb-3 p-2 rounded-lg transition-colors ${
                r.last_clip_url ? "hover:bg-white/5 cursor-pointer" : ""
              }`}
            >
              <span
                className={`px-2 py-1 rounded-md text-sm font-semibold ${
                  formatTimeAgo(r.last_seen).includes("h")
                    ? "bg-yellow-500/80 text-black"
                    : "bg-emerald-500/80 text-black"
                }`}
              >
                {formatTimeAgo(r.last_seen)}
              </span>
              <div className="flex items-center gap-2">
                {displayName(r.id, r.name)}
                {r.last_clip_url && (
                  <span className="text-xs text-gray-500">▶</span>
                )}
                <span className="text-gray-400 text-sm">
                  {(r.role || "").toLowerCase() === "resident"
                    ? `(Resident ${r.room_number})`
                    : `(${r.role})`}
                </span>
              </div>
            </div>
          ))}
          {peopleOutside.length === 0 && (
            <div className="text-center text-gray-400 text-sm">No one out of community.</div>
          )}
        </div>
      </div>

      {/* Hover Video Preview */}
      {hoverClip && (
        <div
            className="fixed z-50 rounded-xl overflow-hidden shadow-2xl border border-white/20 bg-black"
            style={{
                left: Math.min(hoverClip.x, window.innerWidth - 280),
                top: Math.max(hoverClip.y, 10),
            }}
        >
            <div className="px-3 py-1.5 bg-gray-900 text-xs text-gray-300 border-b border-white/10">
                {hoverClip.name}
            </div>
            <video
                src={hoverClip.url}
                autoPlay
                loop
                muted
                playsInline
                className="w-64 h-40 object-cover"
            />
        </div>
      )}

      {/* Footer Buttons */}
      <div className="px-6 pb-6 flex gap-4">
        <Button className="flex-1 bg-teal-600 hover:bg-teal-500" onClick={() => setShowHeadcount(true)}>
          Headcount
        </Button>
        <Button className="flex-1 bg-blue-900 hover:bg-blue-800" onClick={() => setShowCensus(true)}>
          Daily Census
        </Button>
      </div>

      {/* Headcount Modal */}
      {showHeadcount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowHeadcount(false)} />
          <div className="relative w-[92vw] max-w-3xl rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold">Headcount</h3>
              <div className="flex items-center gap-2">
                <span className="text-gray-300">as of {formatAsOf(headAsOf)}</span>
                <Button
                  className="bg-slate-700 hover:bg-slate-600"
                  onClick={async () => {
                    try { await fetchResidents(); } finally { setHeadAsOf(new Date()); }
                  }}
                >
                  ⟲ Refresh
                </Button>
                <Button className="bg-slate-700 hover:bg-slate-600" onClick={() => window.print()}>Print</Button>
                <Button className="bg-slate-700 hover:bg-slate-600" onClick={() => setShowHeadcount(false)}>Close</Button>
              </div>
            </div>

            <hr className="border-white/10 mb-6" />

            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-sm text-gray-300 tracking-widest">HEADCOUNT</div>
                <div className="text-7xl font-extrabold leading-none mt-2">{counts.total}</div>
                <div className="text-gray-400 uppercase tracking-widest mt-2">TOTAL</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl p-4 bg-teal-600 text-white">
                <div className="tracking-widest text-sm">RESIDENTS IN</div>
                <div className="text-5xl font-bold mt-2">{counts.rIn}</div>
              </div>
              <div className="rounded-xl p-4 bg-blue-900 text-white">
                <div className="tracking-widest text-sm">RESIDENTS OUT</div>
                <div className="text-5xl font-bold mt-2">{counts.rOut}</div>
              </div>
              <div className="rounded-xl p-4 bg-blue-900 text-white">
                <div className="tracking-widest text-sm">STAFF IN</div>
                <div className="text-5xl font-bold mt-2">{counts.sIn}</div>
              </div>
              <div className="rounded-xl p-4 bg-teal-600 text-white">
                <div className="tracking-widest text-sm">GUESTS IN</div>
                <div className="text-5xl font-bold mt-2">{counts.gIn}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Census Modal */}
      {showCensus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/70" onClick={() => setShowCensus(false)} />
            <div className="relative w-[92vw] max-w-3xl rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold">Daily Census</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-300">as of {formatAsOf(censusAsOf)}</span>
                        <Button
                            className="bg-slate-700 hover:bg-slate-600"
                            onClick={async () => {
                                try { await fetchResidents(); } finally { setCensusAsOf(new Date()); }
                            }}
                        >
                            ⟲ Refresh
                        </Button>
                        <Button
                            className="bg-teal-600 hover:bg-teal-500"
                            onClick={async () => {
                                try {
                                    const res = await apiFetch("/census/send_report", { method: "POST" });
                                    if (!res.ok) throw new Error(String(res.status));
                                    alert("Census report sent to director.");
                                } catch (err) {
                                    alert("Failed to send report.");
                                    console.error(err);
                                }
                            }}
                        >
                            Send Report
                        </Button>
                        <Button className="bg-slate-700 hover:bg-slate-600" onClick={() => window.print()}>Print</Button>
                        <Button className="bg-slate-700 hover:bg-slate-600" onClick={() => setShowCensus(false)}>Close</Button>
                    </div>
                </div>

                <hr className="border-white/10 mb-6" />

                <div className="flex items-center justify-start gap-4 mb-6">
                    <div className="rounded-xl p-4 w-40 bg-teal-600 text-white">
                        <div className="tracking-widest text-sm">SEEN</div>
                        <div className="text-5xl font-bold mt-2">{censusStats.seen}</div>
                    </div>
                    <div className="rounded-xl p-4 w-40 bg-blue-900 text-white">
                        <div className="tracking-widest text-sm">NOT SEEN</div>
                        <div className="text-5xl font-bold mt-2">{censusStats.notSeen}</div>
                    </div>
                    <div className="rounded-xl p-4 w-40 bg-teal-600 text-white">
                        <div className="tracking-widest text-sm">TOTAL</div>
                        <div className="text-5xl font-bold mt-2">{censusStats.total}</div>
                    </div>
                </div>

                <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                    {residentsOnly
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((r) => {
                          const seen = isSeenToday(r);
                          return (
                              <div
                                  key={r.id}
                                  className="w-full flex items-center gap-4 text-left"
                              >
                                  <button
                                      onClick={() => !seen && toggleSeen(r.id)}
                                      disabled={seen}
                                      className={`flex items-center justify-center h-7 w-7 rounded-full border-2 flex-shrink-0 ${
                                          seen
                                              ? "bg-teal-600 border-teal-600 text-white"
                                              : "border-blue-900 text-blue-900 hover:opacity-80"
                                      }`}
                                  >
                                      {seen ? "✓" : ""}
                                  </button>

                                  {/* Tappable name — opens frame modal */}
                                  <button
                                      onClick={() => setCensusFrameResident(r)}
                                      className="flex-1 flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
                                  >
                                      <span className="text-xl">{displayName(r.id, r.name)}</span>
                                      {seen && r.census_last_seen && (
                                          <span className="ml-auto text-sm text-gray-400">
                                              {formatDateTime(r.census_last_seen).time}
                                          </span>
                                      )}
                                      {r.census_last_frame_url && (
                                          <span className="text-xs text-gray-500">📷</span>
                                      )}
                                  </button>
                              </div>
                          );
                      })}
                    {residentsOnly.length === 0 && (
                        <div className="text-gray-400">No residents found.</div>
                    )}
                </div>

                {/* Frame detail modal */}
                {censusFrameResident && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center">
                        <div
                            className="absolute inset-0 bg-black/80"
                            onClick={() => setCensusFrameResident(null)}
                        />
                        <div className="relative w-[92vw] max-w-lg rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-xl font-bold text-white">
                                        {displayName(censusFrameResident.name)}
                                    </h3>
                                    <p className="text-sm text-gray-400">
                                        {censusFrameResident.room_number
                                            ? `Room ${censusFrameResident.room_number}`
                                            : censusFrameResident.role}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setCensusFrameResident(null)}
                                    className="text-gray-400 hover:text-white transition-colors text-2xl leading-none"
                                >
                                    ×
                                </button>
                            </div>

                            <hr className="border-white/10 mb-4" />

                            {/* Frame or placeholder */}
                            {censusFrameResident.census_last_frame_url ? (
                                <img
                                    src={`https://api.monivue.io${censusFrameResident.census_last_frame_url}`}
                                    alt={displayName(censusFrameResident.name)}
                                    className="w-full rounded-lg object-cover mb-4"
                                />
                            ) : (
                                <div className="w-full h-48 rounded-lg bg-gray-800 flex items-center justify-center mb-4">
                                    <p className="text-gray-400 text-sm">No frame available</p>
                                </div>
                            )}

                            {/* Seen time */}
                            {isSeenToday(censusFrameResident) && censusFrameResident.census_last_seen && (
                                <p className="text-sm text-gray-400 text-center mb-4">
                                    Last seen at {formatDateTime(censusFrameResident.census_last_seen).time}
                                    {" • "}
                                    {censusFrameResident.census_last_clip_url ? (
                                      <button
                                          onClick={() => window.open(
                                              "https://api.monivue.io" + censusFrameResident.census_last_clip_url,
                                              "_blank"
                                          )}
                                          className="text-teal-400 hover:text-teal-300 underline"
                                      >
                                          View clip ▶
                                      </button>
                                  ) : "No clip"}
                                </p>
                            )}

                            {/* Action buttons */}
                            <div className="flex gap-3">
                                <Button
                                    className="flex-1 bg-gray-700 hover:bg-gray-600"
                                    onClick={() => setCensusFrameResident(null)}
                                >
                                    Close
                                </Button>
                                {isSeenToday(censusFrameResident) ? (
                                    <Button
                                        className="flex-1 bg-rose-700 hover:bg-rose-600"
                                        onClick={() => toggleUnseen(censusFrameResident.id)}
                                    >
                                        Remove from Seen
                                    </Button>
                                ) : (
                                    <Button
                                        className="flex-1 bg-teal-600 hover:bg-teal-500"
                                        onClick={async () => {
                                            await toggleSeen(censusFrameResident.id);
                                            setCensusFrameResident(null);
                                        }}
                                    >
                                        Mark as Seen
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
      )}
    </div>
  );
}